// EditInspectionModal.tsx
import React, {FC, useEffect, useMemo, useState} from 'react'
import type {
  QhseInspectionKind,
  Port,
  VesselLite,
  CrewLite,
  InspectionDto,
  InspectionAttachmentInfo,
} from '../core/_models'
import RichTextEditor from '../components/RichTextEditor'
import {
  deleteInspectionAttachment,
  listAvailablePlans,
  listUnlinkedPlans,
  getInspectionPlan,
  getPlanByInspection,
} from '../core/_requests'
import {toast} from 'react-toastify'
import SearchableSelect from '../../../shared/components/SearchableSelect'

export type PlanOption = {id: number; label: string}
export type SubmitShape = {
  vessel: string
  inspection: string
  inspectionType?: string
  inspectionDate: string
  internalInspectorId?: string
  externalInspector?: string
  fromPort?: string
  toPort?: string
  hoursOnboard?: number
  linkToPlan?: string
  inspectionRemarks?: string
  attachmentFiles?: File[] | null
}

export const EditInspectionModal: FC<{
  visible: boolean
  onClose: () => void
  record: InspectionDto | null
  onSubmit: (data: SubmitShape) => void
  vessels: VesselLite[]
  kinds: QhseInspectionKind[]
  ports: Port[]
  planOptions?: PlanOption[]
  crews: CrewLite[]
  companies: Array<{id: number; name: string}>
  subcompanies: Array<{id: number; name: string; companyId: number}>
  rankMap?: Record<number, string>

  /** NEW role-aware flags */
  showCompanyFilters?: boolean
  isCrew?: boolean
  disableVesselChange?: boolean
}> = ({
  visible,
  onClose,
  record,
  onSubmit,
  vessels,
  kinds,
  ports,
  planOptions = [],
  crews,
  companies,
  subcompanies,
  rankMap = {},
  showCompanyFilters = true,
  isCrew = false,
  disableVesselChange = false,
}) => {
  const LABEL_CLS = 'form-label fw-semibold fs-6 mb-2 text-dark'

  const WRAP_STYLE: React.CSSProperties = {
    background: 'rgba(0,0,0,0.5)',
    position: 'fixed',
    inset: 0,
    zIndex: 1050,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const MODAL_DIALOG_STYLE: React.CSSProperties = {
    maxWidth: '95%',
    width: 'auto',
    margin: '0 auto',
  }

  const MODAL_CONTENT_STYLE: React.CSSProperties = {
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
  }

  const [companyId, setCompanyId] = useState<string>('') // CGA
  const [subcompanyId, setSubcompanyId] = useState<string>('') // Company

  const [formData, setFormData] = useState({
    vessel: '',
    inspection: '',
    inspectionType: 'DETAILS_TYPE',
    inspectionDate: '',
    externalInspector: '',
    fromPort: '',
    toPort: '',
    hoursOnboard: '',
    linkToPlan: '',
    inspectionRemarks: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([])
  const [existingAttachments, setExistingAttachments] = useState<InspectionAttachmentInfo[]>([])

  const [currentLinkedPlan, setCurrentLinkedPlan] = useState<any>(null)
  const [unlinkedPlans, setUnlinkedPlans] = useState<any[]>([])
  const [availablePlans, setAvailablePlans] = useState<Array<{id: number; label: string}>>([])

  const sameMonth = (isoA?: string, isoB?: string) => {
    if (!isoA || !isoB) return false
    const a = new Date(isoA)
    const b = new Date(isoB)
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
  }

  const firstDayOfMonth = (iso: string) => {
    const d = new Date(iso)
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    return `${yyyy}-${mm}-01`
  }

  const vesselNameById = (id?: string | number) => {
    const n = Number(id)
    if (!n) return '—'
    const v = vessels.find(v => Number(v.id) === n)
    return v?.name || '—'
  }

  const kindNameById = (id?: string | number) => {
    const n = Number(id)
    if (!n) return '—'
    const k = kinds.find(k => Number(k.id) === n)
    return k?.name || '—'
  }

  // prefill
  useEffect(() => {
    if (!record) return
    const v = vessels.find(vv => Number(vv.id) === Number(record.vesselId))
    const cga = v?.companyGroupAdminId ? String(v?.companyGroupAdminId) : ''
    const ca = v?.companyAdminId ? String(v?.companyAdminId) : ''
    setCompanyId(cga)
    setSubcompanyId(ca)

    const rawPlanId =
      record.planId != null
        ? String(record.planId)
        : (record as any).inspectionPlanId != null
        ? String((record as any).inspectionPlanId)
        : ''

    setFormData({
      vessel: String(record.vesselId || ''),
      inspection: String(record.inspectionKindId || ''),
      inspectionType: String(record.inspectionType || 'DETAILS_TYPE'),
      inspectionDate: record.inspectionFromDate || '',
      externalInspector: record.externalInspectorName || '',
      fromPort: record.fromPortId != null ? String(record.fromPortId) : '',
      toPort: record.toPortId != null ? String(record.toPortId) : '',
      hoursOnboard: record.hoursOnboard != null ? String(record.hoursOnboard) : '',
      linkToPlan: rawPlanId,
      inspectionRemarks: record.remarks || '',
    })
    setAttachmentFiles([])
    setExistingAttachments(Array.isArray(record.attachments) ? record.attachments : [])
    setErrors({})

    let active = true
    ;(async () => {
      let p: any = null
      if (rawPlanId) {
        try {
          p = await getInspectionPlan(Number(rawPlanId))
        } catch {}
      }
      if (!p && record.id) {
        try {
          p = await getPlanByInspection(Number(record.id))
        } catch {}
      }
      if (active) {
        if (p) {
          setCurrentLinkedPlan(p)
          setFormData(prev => ({
            ...prev,
            linkToPlan: String(p.id),
          }))
        } else {
          setCurrentLinkedPlan(null)
        }
      }
    })()

    return () => {
      active = false
    }
  }, [record, vessels])

  // Load unlinked plans for current year
  useEffect(() => {
    if (!visible) return
    ;(async () => {
      try {
        const year = new Date().getFullYear()
        const list = await listUnlinkedPlans({year})
        setUnlinkedPlans(Array.isArray(list) ? list : [])
      } catch {
        setUnlinkedPlans([])
      }
    })()
  }, [visible])

  // Plan consistency
  useEffect(() => {
    const pid = formData.linkToPlan
    if (!pid) return

    const chosen =
      currentLinkedPlan && String(currentLinkedPlan.id) === String(pid)
        ? currentLinkedPlan
        : unlinkedPlans.find(p => String(p.id) === String(pid))

    if (!chosen) {
      if (availablePlans.some(p => String(p.id) === String(pid))) return
      setFormData(prev => ({...prev, linkToPlan: ''}))
      return
    }

    const vOk = !formData.vessel || String(formData.vessel) === String(chosen.vesselId)
    const kOk =
      !formData.inspection || String(formData.inspection) === String(chosen.inspectionKindId)
    const monthOk =
      !formData.inspectionDate ||
      sameMonth(firstDayOfMonth(formData.inspectionDate), firstDayOfMonth(chosen.planDate))

    if (!vOk || !kOk || !monthOk) {
      setFormData(prev => ({...prev, linkToPlan: ''}))
    }
  }, [
    formData.vessel,
    formData.inspection,
    formData.inspectionDate,
    formData.linkToPlan,
    unlinkedPlans,
    currentLinkedPlan,
    availablePlans,
  ])

  // Derive plan dropdown options
  useEffect(() => {
    const v = Number(formData.vessel || 0)
    const k = Number(formData.inspection || 0)
    const iso = formData.inspectionDate

    const formatPlanLabel = (p: any, isCurrent = false) =>
      `${p.planDate} • ${vesselNameById(p.vesselId)} • ${kindNameById(p.inspectionKindId)}${
        isCurrent ? ' (Current)' : ''
      }`

    if (v && k && iso) {
      const forDate = firstDayOfMonth(iso)
      ;(async () => {
        try {
          const res = await listAvailablePlans({vesselId: v, inspectionKindId: k, forDate})
          let opts = (Array.isArray(res) ? res : []).map((p: any) => ({
            id: Number(p.id),
            label: formatPlanLabel(p),
          }))

          if (
            currentLinkedPlan &&
            (!v || Number(currentLinkedPlan.vesselId) === v) &&
            (!k || Number(currentLinkedPlan.inspectionKindId) === k) &&
            sameMonth(firstDayOfMonth(currentLinkedPlan.planDate), forDate)
          ) {
            if (!opts.some(o => Number(o.id) === Number(currentLinkedPlan.id))) {
              opts.unshift({
                id: Number(currentLinkedPlan.id),
                label: formatPlanLabel(currentLinkedPlan, true),
              })
            }
          }
          setAvailablePlans(opts)
        } catch {
          if (
            currentLinkedPlan &&
            (!v || Number(currentLinkedPlan.vesselId) === v) &&
            (!k || Number(currentLinkedPlan.inspectionKindId) === k) &&
            sameMonth(firstDayOfMonth(currentLinkedPlan.planDate), forDate)
          ) {
            setAvailablePlans([
              {
                id: Number(currentLinkedPlan.id),
                label: formatPlanLabel(currentLinkedPlan, true),
              },
            ])
          } else {
            setAvailablePlans([])
          }
        }
      })()
      return
    }

    let pool = [...unlinkedPlans]
    if (v) pool = pool.filter((p: any) => Number(p.vesselId) === v)
    if (k) pool = pool.filter((p: any) => Number(p.inspectionKindId) === k)
    if (iso) {
      const month01 = firstDayOfMonth(iso)
      pool = pool.filter((p: any) => sameMonth(p.planDate, month01))
    }

    let opts = pool.map((p: any) => ({
      id: Number(p.id),
      label: formatPlanLabel(p),
    }))

    if (
      currentLinkedPlan &&
      (!v || Number(currentLinkedPlan.vesselId) === v) &&
      (!k || Number(currentLinkedPlan.inspectionKindId) === k) &&
      (!iso || sameMonth(firstDayOfMonth(currentLinkedPlan.planDate), firstDayOfMonth(iso)))
    ) {
      if (!opts.some(o => Number(o.id) === Number(currentLinkedPlan.id))) {
        opts.unshift({
          id: Number(currentLinkedPlan.id),
          label: formatPlanLabel(currentLinkedPlan, true),
        })
      }
    }
    setAvailablePlans(opts)
  }, [
    unlinkedPlans,
    vessels,
    kinds,
    formData.vessel,
    formData.inspection,
    formData.inspectionDate,
    currentLinkedPlan,
  ])

  const vesselsForModal = useMemo(() => {
    let list = vessels
    if (companyId) {
      const cid = Number(companyId)
      list = list.filter(
        v =>
          Number(
            (v as any).companyGroupAdminId ?? (v as any).companyGroupId
          ) === cid
      )
    }
    if (subcompanyId) {
      const scid = Number(subcompanyId)
      list = list.filter(
        v =>
          Number(
            (v as any).companyAdminId ?? (v as any).companyId
          ) === scid
      )
    }
    return list
  }, [vessels, companyId, subcompanyId])

  const subcompaniesForCompany = useMemo(() => {
    if (!companyId) return []
    const cid = Number(companyId)
    return subcompanies.filter(sc => sc.companyId === cid)
  }, [subcompanies, companyId])
  const hasSubcompaniesForCompany = subcompaniesForCompany.length > 0

  // keep vessel in sync when editable
  useEffect(() => {
    if (disableVesselChange) return
    const current = Number(formData.vessel || 0)
    const ids = new Set(vesselsForModal.map(v => Number(v.id)))
    if (!current || !ids.has(current)) {
      setFormData(prev => ({...prev, vessel: ''}))
    }
    if (vesselsForModal.length === 1) {
      const onlyId = String(vesselsForModal[0].id)
      setFormData(prev =>
        prev.vessel === onlyId
          ? prev
          : {...prev, vessel: onlyId}
      )
    }
  }, [vesselsForModal, disableVesselChange]) // eslint-disable-line

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const {name, value} = e.target
    setFormData(prev => ({...prev, [name]: value}))
    if (errors[name]) setErrors(prev => ({...prev, [name]: ''}))
  }

  const validate = () => {
    const er: Record<string, string> = {}
    if (!formData.vessel.trim()) er.vessel = 'Vessel is required'
    if (!formData.inspection.trim()) er.inspection = 'Inspection Type is required'
    if (!formData.inspectionDate.trim()) er.inspectionDate = 'Inspection Date is required'
    if (
      formData.hoursOnboard &&
      (isNaN(Number(formData.hoursOnboard)) || Number(formData.hoursOnboard) < 0)
    ) {
      er.hoursOnboard = 'Hours Onboard must be a positive number'
    }
    setErrors(er)
    return Object.keys(er).length === 0
  }

  const submit = () => {
    if (!validate()) return
    onSubmit({
      vessel: formData.vessel,
      inspection: formData.inspection,
      inspectionType: 'DETAILS_TYPE',
      inspectionDate: formData.inspectionDate,
      internalInspectorId: undefined,
      externalInspector: formData.externalInspector || undefined,
      fromPort: formData.fromPort || undefined,
      toPort: formData.toPort || undefined,
      hoursOnboard: formData.hoursOnboard
        ? Number(formData.hoursOnboard)
        : undefined,
      linkToPlan: formData.linkToPlan || undefined,
      inspectionRemarks: formData.inspectionRemarks || undefined,
      attachmentFiles: attachmentFiles.length ? attachmentFiles : null,
    })
  }

  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : []
    if (files.length) {
      setAttachmentFiles((prev) => [...prev, ...files])
    }
    e.target.value = ''
  }

  const removeSelectedAttachment = (index: number) => {
    setAttachmentFiles((prev) => prev.filter((_, currentIndex) => currentIndex !== index))
  }

  const removeExistingAttachment = async (attachmentId: number) => {
    if (!record?.id) return
    try {
      await deleteInspectionAttachment(Number(record.id), attachmentId)
      setExistingAttachments((prev) => prev.filter((attachment) => attachment.id !== attachmentId))
      toast.success('Attachment removed', {position: 'top-center'})
    } catch (error: any) {
      toast.error('Failed to remove attachment', {
        position: 'top-center',
      })
    }
  }

  if (!visible || !record) return null

  const lockedVesselId = formData.vessel || String(record.vesselId || '')

  return (
    <div
      className='modal fade show d-flex align-items-center justify-content-center'
      tabIndex={-1}
      style={WRAP_STYLE}
    >
      <div
        className='modal-dialog modal-xl modal-dialog-centered'
        role='dialog'
        style={MODAL_DIALOG_STYLE}
      >
        <div
          className='modal-content bg-white text-dark'
          style={MODAL_CONTENT_STYLE}
        >
          <div className='modal-header'>
            <h5 className='modal-title text-dark'>Edit Inspection</h5>
            <button type='button' className='btn-close' onClick={onClose} />
          </div>

          <div className='modal-body' style={{overflowY: 'auto'}}>
            <div className='row g-3'>
              {/* Company (disabled) */}
              {showCompanyFilters && (
                <div className='col-md-6'>
                  <label className={LABEL_CLS}>Company</label>
                  <SearchableSelect
                    className='form-select text-dark'
                    value={companyId}
                    onChange={e => {
                      setCompanyId(e.target.value)
                      setSubcompanyId('')
                    }}
                    disabled={true}
                    style={{background: '#f8f9fa', cursor: 'not-allowed'}}
                  >
                    <option value=''>All Companies</option>
                    {companies.map(c => (
                      <option key={c.id} value={String(c.id)}>
                        {c.name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              )}

              {/* Subcompany (disabled) */}
              {showCompanyFilters && Boolean(companyId) && hasSubcompaniesForCompany && (
                <div className='col-md-6'>
                  <label className={LABEL_CLS}>Subcompany</label>
                  <SearchableSelect
                    className='form-select text-dark'
                    value={subcompanyId}
                    onChange={e => setSubcompanyId(e.target.value)}
                    disabled={true}
                    style={{background: '#f8f9fa', cursor: 'not-allowed'}}
                  >
                    <option value=''>All Subcompanies</option>
                    {subcompaniesForCompany.map(sc => (
                      <option key={sc.id} value={String(sc.id)}>
                        {sc.name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              )}

              {/* Vessel */}
              <div className='col-md-6'>
                <label className={LABEL_CLS}>
                  Vessel <span className='text-danger'>*</span>
                </label>
                {disableVesselChange ? (
                  <>
                    <div
                      className='form-control'
                      style={{background: '#f8f9fa'}}
                      title='Locked by role'
                    >
                      {vesselNameById(lockedVesselId)}
                    </div>
                    <input type='hidden' name='vessel' value={lockedVesselId} />
                  </>
                ) : vesselsForModal.length === 1 ? (
                  <>
                    <input
                      type='hidden'
                      name='vessel'
                      value={formData.vessel || String(vesselsForModal[0].id)}
                    />
                    <div className='form-control' style={{background: '#f8f9fa'}}>
                      {vesselsForModal[0].name}
                    </div>
                  </>
                ) : (
                  <SearchableSelect
                    className={`form-select text-dark ${
                      errors.vessel ? 'is-invalid' : ''
                    }`}
                    name='vessel'
                    value={formData.vessel}
                    onChange={handleChange}
                  >
                    <option value=''>Select Vessel</option>
                    {vesselsForModal.map(v => (
                      <option key={v.id} value={String(v.id)}>
                        {v.name}
                      </option>
                    ))}
                  </SearchableSelect>
                )}
                {errors.vessel && (
                  <div className='invalid-feedback'>{errors.vessel}</div>
                )}
              </div>

              {/* Inspection Type */}
              <div className='col-md-6'>
                <label className={LABEL_CLS}>
                  Inspection Type <span className='text-danger'>*</span>
                </label>
                <SearchableSelect
                  className={`form-select text-dark ${
                    errors.inspection ? 'is-invalid' : ''
                  }`}
                  name='inspection'
                  value={formData.inspection}
                  onChange={handleChange}
                >
                  <option value=''>Select Inspection Type</option>
                  {kinds.map(k => (
                    <option key={k.id} value={String(k.id)}>
                      {k.name}
                    </option>
                  ))}
                </SearchableSelect>
                {errors.inspection && (
                  <div className='invalid-feedback'>{errors.inspection}</div>
                )}
              </div>

              {/* Inspection Date */}
              <div className='col-md-6'>
                <label className={LABEL_CLS}>
                  Inspection Date <span className='text-danger'>*</span>
                </label>
                <input
                  type='date'
                  className={`form-control text-dark ${
                    errors.inspectionDate ? 'is-invalid' : ''
                  }`}
                  name='inspectionDate'
                  value={formData.inspectionDate}
                  onChange={handleChange}
                />
                {errors.inspectionDate && (
                  <div className='invalid-feedback'>
                    {errors.inspectionDate}
                  </div>
                )}
              </div>

              {/* Inspector Name */}
              <div className='col-md-6'>
                <label className={LABEL_CLS}>Inspector Name</label>
                <input
                  type='text'
                  className='form-control text-dark'
                  name='externalInspector'
                  value={formData.externalInspector}
                  onChange={handleChange}
                  placeholder='Enter inspector name'
                />
              </div>

              {/* Ports */}
              <div className='col-md-6'>
                <label className={LABEL_CLS}>From Port</label>
                <SearchableSelect
                  className='form-select text-dark'
                  name='fromPort'
                  value={formData.fromPort}
                  onChange={handleChange}
                >
                  <option value=''>Select From Port</option>
                  {ports.map(p => (
                    <option key={p.id} value={String(p.id)}>
                      {p.name}
                    </option>
                  ))}
                </SearchableSelect>
              </div>

              <div className='col-md-6'>
                <label className={LABEL_CLS}>To Port</label>
                <SearchableSelect
                  className='form-select text-dark'
                  name='toPort'
                  value={formData.toPort}
                  onChange={handleChange}
                >
                  <option value=''>Select To Port</option>
                  {ports.map(p => (
                    <option key={p.id} value={String(p.id)}>
                      {p.name}
                    </option>
                  ))}
                </SearchableSelect>
              </div>

              {/* Hours */}
              <div className='col-md-6'>
                <label className={LABEL_CLS}>Hours Onboard</label>
                <input
                  type='number'
                  className={`form-control text-dark ${
                    errors.hoursOnboard ? 'is-invalid' : ''
                  }`}
                  name='hoursOnboard'
                  value={formData.hoursOnboard}
                  onChange={handleChange}
                  min='0'
                  step='0.5'
                />
                {errors.hoursOnboard && (
                  <div className='invalid-feedback'>{errors.hoursOnboard}</div>
                )}
              </div>

              {/* Link to Plan */}
              <div className='col-md-6'>
                <label className={LABEL_CLS}>Link to Plan</label>
                <SearchableSelect
                  className='form-select text-dark'
                  name='linkToPlan'
                  value={formData.linkToPlan}
                  onChange={handleChange}
                >
                  <option value=''>Select Plan</option>
                  {availablePlans.map(plan => (
                    <option key={plan.id} value={String(plan.id)}>
                      {plan.label}
                    </option>
                  ))}
                </SearchableSelect>
              </div>

              {/* Remarks */}
              <div className='col-12'>
                <label className={LABEL_CLS}>Inspection Remarks</label>
                <div style={{paddingBottom: '50px'}}>
                  <RichTextEditor
                    value={formData.inspectionRemarks}
                    onChange={html => {
                      setFormData(prev => ({...prev, inspectionRemarks: html}))
                      if (errors.inspectionRemarks) {
                        setErrors(prev => ({...prev, inspectionRemarks: ''}))
                      }
                    }}
                    placeholder='Update notes, bullets, links…'
                    invalid={Boolean(errors.inspectionRemarks)}
                    height={220}
                  />
                </div>
                {errors.inspectionRemarks && (
                  <div className='invalid-feedback d-block'>
                    {errors.inspectionRemarks}
                  </div>
                )}
              </div>

              <div className='col-12'>
                <label className={LABEL_CLS}>Attachments</label>
                <input type='file' className='form-control' multiple onChange={handleAttachmentChange} />
                {existingAttachments.length > 0 ? (
                  <div className='mt-3 d-flex flex-column gap-2'>
                    {existingAttachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className='d-flex align-items-center justify-content-between border rounded px-3 py-2'
                      >
                        <span className='text-truncate pe-3'>{attachment.originalFileName}</span>
                        <button
                          type='button'
                          className='btn btn-light-danger btn-sm'
                          onClick={() => removeExistingAttachment(attachment.id)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
                {attachmentFiles.length > 0 ? (
                  <div className='mt-3 d-flex flex-column gap-2'>
                    {attachmentFiles.map((file, index) => (
                      <div
                        key={`${file.name}-${index}`}
                        className='d-flex align-items-center justify-content-between border rounded px-3 py-2'
                      >
                        <span className='text-truncate pe-3'>{file.name}</span>
                        <button
                          type='button'
                          className='btn btn-light-danger btn-sm'
                          onClick={() => removeSelectedAttachment(index)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
                {existingAttachments.length === 0 && attachmentFiles.length === 0 ? (
                  <div className='text-muted fs-7 mt-2'>No attachments available.</div>
                ) : null}
              </div>
            </div>
          </div>

          <div className='modal-footer'>
            <button
              type='button'
              className='btn btn-light btn-sm'
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type='button'
              className='btn btn_primary'
              onClick={submit}
            >
              Update
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditInspectionModal
